// Web v2 sections, group A (WEB_V2_BUILD.md §2, workstream S1): the logic the
// components and the server share, kept free of Vue and the database so
// node --test can run it. Opening hours and the postcode check mirror
// bristol's domain/websiteData.js (D1) so both sides give the same answer.

export const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const DAY_NAMES = {mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday"};
const DAY_SHORT = {mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun"};

// ---- opening hours ----------------------------------------------------------------

export function localParts(now, timeZone = "Europe/London") {
    const parts = Object.fromEntries(new Intl.DateTimeFormat("en-GB", {
        timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", weekday: "short", hour12: false,
    }).formatToParts(now).map((p) => [p.type, p.value]));
    const hour = parts.hour === "24" ? "00" : parts.hour;
    return {date: `${parts.year}-${parts.month}-${parts.day}`, time: `${hour}:${parts.minute}`, day: parts.weekday.toLowerCase().slice(0, 3)};
}

const span = (c) => Date.parse(c.to) - Date.parse(c.from);

// The ranges that apply on a date: the shortest closure covering it wins
// (its special hours, or none), otherwise the week.
export function rangesOn(hours, date, day) {
    const closure = (hours?.closures || []).filter((c) => c.from <= date && date <= c.to).sort((a, b) => span(a) - span(b))[0];
    if (closure) return {ranges: closure.hours || [], closure};
    return {ranges: hours?.week?.[day] || [], closure: null};
}

// {open, until?, opensAt?, opensDay?, closure} — same answers as bristol's openNow.
export function openNow(hours, now = new Date(), timeZone = "Europe/London") {
    if (!hasHours(hours)) return null;
    const {date, time, day} = localParts(now, timeZone);
    const {ranges, closure} = rangesOn(hours, date, day);
    const current = ranges.find((r) => r.open <= time && time < r.close);
    if (current) return {open: true, until: current.close, closure: closure?.label || null};
    const later = ranges.find((r) => r.open > time);
    if (later) return {open: false, opensAt: later.open, opensDay: "today", closure: closure?.label || null};
    for (let i = 1; i <= 14; i++) {
        const d = new Date(now.getTime() + i * 86400000);
        const p = localParts(d, timeZone);
        const next = rangesOn(hours, p.date, p.day).ranges[0];
        if (next) return {open: false, opensAt: next.open, opensDay: i === 1 ? "tomorrow" : p.day, closure: closure?.label || null};
    }
    return {open: false, closure: closure?.label || null};
}

export const upcomingClosures = (hours, now = new Date(), timeZone = "Europe/London") => {
    const {date} = localParts(now, timeZone);
    return (hours?.closures || []).filter((c) => c.to >= date);
};

export function hasHours(hours) {
    return !!hours && typeof hours === "object" && DAYS.some((d) => Array.isArray(hours.week?.[d]) && hours.week[d].length > 0);
}

// "08:30" -> "8:30am", "16:00" -> "4pm", "12:00" -> "12pm".
export function niceTime(t) {
    if (typeof t !== "string" || !/^\d{2}:\d{2}$/.test(t)) return "";
    let [h, m] = t.split(":").map(Number);
    const suffix = h >= 12 ? "pm" : "am";
    h = h % 12 || 12;
    return m ? `${h}:${String(m).padStart(2, "0")}${suffix}` : `${h}${suffix}`;
}

export const niceRanges = (ranges) => (Array.isArray(ranges) && ranges.length
    ? ranges.map((r) => `${niceTime(r.open)} – ${niceTime(r.close)}`).join(", ")
    : "Closed");

// The week as rows, days with the same hours run together: "Mon – Thu  8am – 4pm".
export function weekRows(hours) {
    if (!hasHours(hours)) return [];
    const rows = [];
    for (const day of DAYS) {
        const text = niceRanges(hours.week?.[day]);
        const last = rows[rows.length - 1];
        if (last && last.text === text) {
            last.to = day;
        } else {
            rows.push({from: day, to: day, text});
        }
    }
    return rows.map((r) => ({
        days: r.from === r.to ? DAY_SHORT[r.from] : `${DAY_SHORT[r.from]} – ${DAY_SHORT[r.to]}`,
        text: r.text,
        includes: (day) => DAYS.indexOf(day) >= DAYS.indexOf(r.from) && DAYS.indexOf(day) <= DAYS.indexOf(r.to),
        from: r.from, to: r.to,
    }));
}

// "Open now · till 4pm" / "Closed · opens 8am tomorrow" / "Closed for Christmas · opens Fri 9am".
export function openNowText(state) {
    if (!state) return "";
    if (state.open) return `Open now · till ${niceTime(state.until)}`;
    const why = state.closure ? `Closed for ${state.closure.replace(/^closed( for)?\s*/i, "")}` : "Closed";
    if (!state.opensAt) return why;
    const when = state.opensDay === "today" ? "" : state.opensDay === "tomorrow" ? " tomorrow" : ` ${DAY_SHORT[state.opensDay] || state.opensDay}`;
    return `${why} · opens ${niceTime(state.opensAt)}${when}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function niceDate(iso) {
    if (typeof iso !== "string" || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return "";
    const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
    const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    return `${["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][wd]} ${d} ${MONTHS[m - 1]}`;
}

export function closureText(c) {
    const when = c.from === c.to ? niceDate(c.from) : `${niceDate(c.from)} – ${niceDate(c.to)}`;
    const what = Array.isArray(c.hours) && c.hours.length ? niceRanges(c.hours) : "Closed";
    return {when, what, label: c.label || ""};
}

// ---- postcode check ("Do we cover you?") -------------------------------------------

export const district = (postcode) => {
    const s = String(postcode || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (!s) return "";
    if (/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(s)) return s.slice(0, -3);
    return s;
};
const matches = (d, list) => list.some((a) => d === a || (/^[A-Z]{1,2}$/.test(a) && d.replace(/\d.*$/, "") === a));

// 'yes' | 'near' | 'no' | 'unknown' (not a postcode).
export function coverCheck(areas, postcode) {
    const d = district(postcode);
    if (!/^[A-Z]{1,2}\d[A-Z\d]?$/.test(d)) return "unknown";
    if (matches(d, areas?.covered || [])) return "yes";
    if (matches(d, areas?.near || [])) return "near";
    return "no";
}

// ---- prices -------------------------------------------------------------------------

const money = (v) => {
    if (v === null || v === undefined || v === "") return "";
    if (typeof v === "number" && Number.isFinite(v)) {
        const whole = Math.round(v * 100) % 100 === 0;
        return `£${v.toLocaleString("en-GB", {minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2})}`;
    }
    const s = String(v).trim();
    if (/^\d{1,3}(,\d{3})+(\.\d{1,2})?$/.test(s) || /^\d+(\.\d{1,2})?$/.test(s)) return money(Number(s.replace(/,/g, "")));
    return s; // "£45", "from £12", "POA" — typed as the business wants it shown
};
export const priceText = money;

// A price list's items, from structured rows or the studio's "one per line:
// name, price" text. Unknown shapes are dropped, never guessed.
export function priceItems(items) {
    if (Array.isArray(items)) {
        return items.map((i) => (typeof i === "string" ? lineItem(i) : {
            name: String(i?.name ?? i?.title ?? "").trim(),
            price: money(i?.price),
            from: i?.from === true,
            note: String(i?.note ?? i?.description ?? "").trim(),
        })).filter((i) => i && i.name);
    }
    if (typeof items === "string") return items.split(/\r?\n/).map(lineItem).filter((i) => i && i.name);
    return [];
}
function lineItem(line) {
    const s = String(line || "").trim();
    if (!s) return null;
    const m = s.match(/^(.*?)[,\t–-]\s*((?:from\s+)?£?\s*\d[\d,]*(?:\.\d{1,2})?)\s*(?:[,(]\s*(.*?)\)?)?$/i);
    if (!m) return {name: s, price: "", from: false, note: ""};
    const raw = m[2].trim();
    const from = /^from\s/i.test(raw);
    const num = raw.replace(/^from\s+/i, "").replace(/[£,\s]/g, "");
    return {name: m[1].trim(), price: money(num), from, note: (m[3] || "").trim()};
}

export function priceGroups(section, products = null) {
    if (section?.fromProducts && Array.isArray(products)) {
        const wanted = section.productCategoryId ? products.filter((p) => p.categoryId === section.productCategoryId) : products;
        const byGroup = new Map();
        for (const p of wanted) {
            const key = p.category || "Prices";
            if (!byGroup.has(key)) byGroup.set(key, []);
            byGroup.get(key).push({name: p.name, price: money(p.price), from: false, note: p.note || ""});
        }
        return [...byGroup.entries()].map(([name, items]) => ({name, items}));
    }
    const groups = Array.isArray(section?.groups) ? section.groups : [];
    return groups.map((g) => ({name: String(g?.name ?? g?.title ?? "").trim(), items: priceItems(g?.items)}))
        .filter((g) => g.items.length);
}

export function packageList(section) {
    const list = Array.isArray(section?.packages) ? section.packages : [];
    return list.map((p, i) => ({
        name: String(p?.name ?? p?.title ?? "").trim(),
        price: money(p?.price),
        note: String(p?.note ?? p?.description ?? "").trim(),
        features: (Array.isArray(p?.features) ? p.features : String(p?.features ?? "").split(/\r?\n/))
            .map((f) => String(typeof f === "object" ? (f?.title ?? f?.text ?? "") : f).trim()).filter(Boolean),
        highlight: p?.highlight === true || (section?.highlight === true && list.length === 3 && i === 1),
        button: String(p?.button ?? "").trim(),
        link: String(p?.link ?? "").trim(),
    })).filter((p) => p.name);
}

// ---- reviews ------------------------------------------------------------------------

// Which reviews a section shows, from the business's approved reviews (newest
// first or best first as bristol sorts them). Never invents any.
export function pickReviews(all, section = {}) {
    const list = Array.isArray(all) ? all.filter((r) => r && r.body) : [];
    const count = Math.max(1, Math.min(24, Number(section.count) || 6));
    const which = section.which || "best";
    let chosen;
    if (which === "picked" && Array.isArray(section.picked) && section.picked.length) {
        const ids = section.picked.map((p) => (typeof p === "object" ? p?.id : p)).filter(Boolean);
        chosen = ids.map((id) => list.find((r) => r.id === id)).filter(Boolean);
    } else if (which === "word" && section.word) {
        const w = String(section.word).toLowerCase();
        chosen = list.filter((r) => String(r.body).toLowerCase().includes(w));
    } else if (which === "newest") {
        chosen = [...list].sort((a, b) => String(b.review_date || b.created_at || "").localeCompare(String(a.review_date || a.created_at || "")));
    } else {
        chosen = [...list].sort((a, b) => (b.rating || 0) - (a.rating || 0) || (b.featured === true) - (a.featured === true)
            || String(b.review_date || "").localeCompare(String(a.review_date || "")));
    }
    return chosen.slice(0, count);
}

// Hidden on the live site until there are enough reviews (minReviews).
export function reviewsShown(all, section = {}) {
    const min = Math.max(0, Number(section.minReviews ?? 0) || 0);
    return (Array.isArray(all) ? all.length : 0) >= min;
}

export const stars = (rating) => {
    const n = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    return "★".repeat(n) + "☆".repeat(5 - n);
};

// ---- numbers --------------------------------------------------------------------------

// Each figure: a typed value, or one Draksy knows (review average/count). A
// figure from data with nothing behind it is left out, never made up.
export function numberItems(section, sources = {}) {
    const items = Array.isArray(section?.items) ? section.items : [];
    return items.map((i) => {
        let value = String(i?.value ?? "").trim();
        let label = String(i?.label ?? i?.title ?? "").trim();
        if (i?.source === "reviews_avg") {
            if (!sources.reviewSummary?.count) return null;
            value = String(sources.reviewSummary.average);
            label = label || `from ${sources.reviewSummary.count} reviews`;
        } else if (i?.source === "reviews_count") {
            if (!sources.reviewSummary?.count) return null;
            value = String(sources.reviewSummary.count);
            label = label || "customer reviews";
        }
        if (!value) return null;
        const m = value.match(/^([^\d]*)(\d[\d,]*(?:\.\d+)?)(.*)$/);
        return {
            value, label,
            prefix: m ? m[1] : "",
            number: m ? m[2].replace(/,/g, "") : null,
            suffix: m ? m[3] : "",
        };
    }).filter(Boolean);
}

// ---- Get a quote -------------------------------------------------------------------

// The preset questions the studio offers (S6C_Leads). Each answer is sent as
// {question, answer}; nothing here is a price.
export const QUOTE_STEPS = {
    what: {question: "What would you like doing?", kind: "choice-text", placeholder: "Tell us a bit about the job"},
    postcode: {question: "Where is it?", kind: "postcode", placeholder: "Your postcode"},
    when: {question: "When do you need us?", kind: "choice", options: ["As soon as possible", "In the next month", "In the next few months", "Just getting prices"]},
    budget: {question: "Do you have a budget in mind?", kind: "choice", options: ["Under £500", "£500 – £2,000", "£2,000 – £10,000", "Over £10,000", "Not sure yet"]},
    photos: {question: "Anything else we should know?", kind: "text", placeholder: "Sizes, access, anything that helps"},
    details: {question: "Tell us more", kind: "text", placeholder: "Anything that helps us quote"},
    address: {question: "What's the address?", kind: "text", placeholder: "House number, street and town"},
    "how-heard": {question: "How did you hear about us?", kind: "choice", options: ["Google", "Facebook or Instagram", "A friend or neighbour", "Saw our van or sign", "Other"]},
    contact: {question: "How do we reach you?", kind: "contact"},
};

export function quoteSteps(section) {
    const keys = Array.isArray(section?.steps) && section.steps.length ? section.steps : ["what", "postcode", "when", "contact"];
    const steps = [];
    for (const k of keys) {
        const key = typeof k === "object" ? (k?.key || k?.preset) : k;
        const custom = typeof k === "object" && k?.question;
        if (custom) {
            const options = (Array.isArray(k.choices) ? k.choices : Array.isArray(k.options) ? k.options : []).map(String).filter(Boolean).slice(0, 12);
            steps.push({key: `custom${steps.length}`, question: String(k.question).slice(0, 200), kind: options.length ? "choice" : "text", options, placeholder: k.placeholder || ""});
        }
        else if (QUOTE_STEPS[key]) steps.push({key, ...QUOTE_STEPS[key]});
    }
    if (!steps.some((s) => s.kind === "contact")) steps.push({key: "contact", ...QUOTE_STEPS.contact});
    // what-choices from the business's own quick tasks or services, when the section has them
    const choices = Array.isArray(section?.whatOptions) ? section.whatOptions.map(String).filter(Boolean).slice(0, 9) : [];
    return steps.map((s) => (s.kind === "choice-text" ? {...s, options: choices} : s));
}

// The answers ready to send: [{question, answer}], contact pulled out.
export function quotePayload(steps, answers) {
    const out = [];
    let name = "", email = "", phone = "";
    for (const s of steps) {
        const a = answers?.[s.key];
        if (s.kind === "contact") {
            name = String(a?.name || "").trim();
            email = String(a?.email || "").trim();
            phone = String(a?.phone || "").trim();
            continue;
        }
        const text = Array.isArray(a) ? a.join(", ") : String(a ?? "").trim();
        if (text) out.push({question: s.question, answer: text.slice(0, 2000)});
    }
    return {name, email, phone, answers: out.slice(0, 30)};
}

export function quoteStepValid(step, value) {
    if (step.kind === "contact") {
        const name = String(value?.name || "").trim();
        const email = String(value?.email || "").trim();
        const phone = String(value?.phone || "").trim();
        return name.length > 0 && (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) || phone.replace(/\D/g, "").length >= 7);
    }
    if (step.kind === "postcode") return /^[A-Z]{1,2}\d[A-Z\d]?$/.test(district(value));
    return true; // other questions are optional
}

// ---- the rest ---------------------------------------------------------------------------

export function listOf(v, max = 24) {
    return Array.isArray(v) ? v.filter((x) => x && typeof x === "object").slice(0, max) : [];
}

// A stored icon value "iconoir:<kebab-name>" -> the bare name, or "" for anything else.
// The renderer looks the name up in the generated icon map; it never builds markup from it.
export function iconoirName(value) {
    const m = /^iconoir:([a-z0-9]+(?:-[a-z0-9]+)*)$/.exec(String(value ?? "").trim());
    return m && m[1].length <= 64 ? m[1] : "";
}

// How each step is marked: its number (the default), nothing, or its icon.
export const markerOf = (section) => (["number", "empty", "icon"].includes(section?.marker) ? section.marker : "number");

export function stepItems(section) {
    return listOf(section?.steps, 8).map((s) => ({title: String(s.title ?? "").trim(), text: String(s.text ?? s.description ?? "").trim(), icon: iconoirName(s.icon) ? `iconoir:${iconoirName(s.icon)}` : ""}))
        .filter((s) => s.title || s.text);
}

export function taskTiles(section) {
    return listOf(section?.tiles, 12).map((t) => ({
        label: String(t.label ?? t.title ?? "").trim(),
        note: String(t.detail ?? t.note ?? t.description ?? "").trim(),
        icon: String(t.icon ?? "").trim(),
        action: ["quote", "link", "call"].includes(t.action) ? t.action : (t.link ? "link" : "quote"),
        link: String(t.link ?? "").trim(),
        quoteAnswer: String(t.quoteAnswer ?? t.label ?? "").trim(),
        dark: t.dark === true,
    })).filter((t) => t.label);
}

// Where a quick task goes: Get a quote with its answer filled in, a page, or a call.
export function taskHref(tile, phone = "") {
    const safe = (l) => (/^(\/|https?:\/\/|tel:|mailto:|#)/i.test(l) ? l : "");
    if (tile.action === "call" && phone) return `tel:${phone.replace(/\s/g, "")}`;
    if (tile.action === "link" && safe(tile.link)) return safe(tile.link);
    return `/contact?what=${encodeURIComponent(tile.quoteAnswer || tile.label)}`;
}

// The ids a section hand-picked: picked:[id | {id}] (or the older which:[...]).
const pickedIds = (section) => {
    const src = section?.which === "picked" && Array.isArray(section.picked) ? section.picked : Array.isArray(section?.which) ? section.which : null;
    return src ? src.map((w) => String(typeof w === "object" ? (w?.id ?? w?.type_id) : w)) : null;
};

// Team: everyone shown on the website, or the ones the section names.
export function pickPeople(all, section = {}) {
    const list = Array.isArray(all) ? all : [];
    const ids = pickedIds(section);
    if (ids && ids.length) return ids.map((id) => list.find((p) => String(p.id) === id)).filter(Boolean);
    return list;
}

export function pickAccreditations(all, section = {}) {
    const list = Array.isArray(all) ? all : [];
    const ids = pickedIds(section);
    if (ids && ids.length) return list.filter((a) => ids.includes(String(a.type_id)));
    return list;
}

// Latest posts: newest published first, optionally one tag.
export function pickPosts(all, section = {}) {
    const count = Math.max(1, Math.min(12, Number(section.count) || 3));
    let list = Array.isArray(all) ? all : [];
    if (section.tag) {
        const tag = String(section.tag).toLowerCase();
        list = list.filter((p) => (p.tags || []).some((t) => String(t).toLowerCase() === tag));
    }
    return list.slice(0, count);
}

// Our work: newest, featured first, or one type.
export function pickProjects(all, section = {}) {
    const count = Math.max(1, Math.min(12, Number(section.count) || 3));
    let list = Array.isArray(all) ? [...all] : [];
    const type = section.type && section.type !== "our-work" ? section.type : section.projectType;
    if (section.which === "type" || section.projectType) {
        if (type) list = list.filter((p) => p.type === type);
    } else if (section.projectType) list = list.filter((p) => p.type === section.projectType);
    if (section.which === "featured") list.sort((a, b) => (b.featured === true) - (a.featured === true));
    if (section.which === "picked" && Array.isArray(section.picked) && section.picked.length) {
        const ids = section.picked.map((p) => (typeof p === "object" ? p?.id : p));
        list = ids.map((id) => list.find((p) => String(p.id) === String(id))).filter(Boolean);
    }
    return list.slice(0, count);
}

export const readingText = (minutes) => (Number(minutes) > 0 ? `${Math.round(Number(minutes))} min read` : "");

// A layout the section names, or its type's first one.
export function layoutOf(section, allowed) {
    return allowed.includes(section?.layout) ? section.layout : allowed[0];
}

// The band's background (S_Variants): plain, tinted, colour, photo.
export const backgroundOf = (section) => (["plain", "tinted", "colour", "photo"].includes(section?.background) ? section.background : "plain");
